import { memo, useEffect, useState } from "react";
import { Chip, Stack } from "@mui/material";
import { ViewHeader } from "../components/ViewHeader";
import { ReissueSelectPatientStep } from "./ReissueSelectPatientStep";
import { ReissueSelectDocumentStep } from "./ReissueSelectDocumentStep";
import { ReissueEditInterventionsStep } from "./ReissueEditInterventionsStep";
import type { ReissuePatientOption } from "./reissuePatientId";
import type { ReissueDocumentType } from "@/pages/patientsearch/components/DocumentReissueShortcutDialog";
import { useInterventionsContext } from "@/contexts/InterventionsContext";

type WizardStep = "patient" | "document";

interface ScheduleDocumentReissuePanelProps {
  /** Quando false (troca de card na agenda), reseta o wizard. */
  isActive: boolean;
}

const STEP_LABELS: Array<{ id: WizardStep; label: string }> = [
  { id: "patient", label: "1. Paciente" },
  { id: "document", label: "2. Documento" },
];

/**
 * Atalho inline na agenda: paciente → documento → Dialog de ajuste / assinatura
 * (um dialog por vez, sem sobreposição).
 */
function ScheduleDocumentReissuePanel({
  isActive,
}: ScheduleDocumentReissuePanelProps) {
  const { setInterventionsList, setBase64 } = useInterventionsContext();

  const [step, setStep] = useState<WizardStep>("patient");
  const [selectedPatient, setSelectedPatient] =
    useState<ReissuePatientOption | null>(null);
  const [selectedType, setSelectedType] = useState<ReissueDocumentType | null>(
    null,
  );
  const [selectedRow, setSelectedRow] = useState<any>(null);
  // Incrementado ao fim da assinatura (sucesso/erro) → ReissueSelectDocumentStep refaz o Bundle
  const [refreshSignal, setRefreshSignal] = useState(0);
  const refreshDocuments = () => setRefreshSignal((n) => n + 1);

  const clearInterventions = () => {
    setInterventionsList([]);
    setBase64(undefined);
  };

  const reissueSessionOpen =
    !!selectedPatient?.patientId &&
    !!selectedRow &&
    !!selectedType &&
    selectedType !== "certificate";

  // Evita estado stale ao sair do card da agenda
  useEffect(() => {
    if (!isActive) {
      setStep("patient");
      setSelectedPatient(null);
      setSelectedType(null);
      setSelectedRow(null);
      clearInterventions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleSelectPatient = (patient: ReissuePatientOption) => {
    if (!patient?.patientId) return;
    setSelectedPatient(patient);
    setSelectedType(null);
    setSelectedRow(null);
    clearInterventions();
    setStep("document");
  };

  const handleBackToPatient = () => {
    setStep("patient");
    setSelectedType(null);
    setSelectedRow(null);
    clearInterventions();
  };

  /** Inicia sessão de remissão (Dialog de ajuste). */
  const handlePrepareReissue = (row: any, type: ReissueDocumentType) => {
    if (type === "certificate") {
      return;
    }
    setSelectedRow(row);
    setSelectedType(type);
  };

  const handleCloseReissueSession = () => {
    setSelectedRow(null);
    setSelectedType(null);
    clearInterventions();
  };

  const handleSignDone = () => {
    setSelectedRow(null);
    setSelectedType(null);
    clearInterventions();
    setStep("document");
  };

  return (
    <Stack direction="column" width="100%" spacing={2}>
      <ViewHeader title="Reemitir Documentos" titleOnly />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {STEP_LABELS.map((item) => {
          const active = step === item.id;
          const stepOrder = ["patient", "document"];
          const currentIdx = stepOrder.indexOf(step);
          const itemIdx = stepOrder.indexOf(item.id);
          const completed = itemIdx < currentIdx;

          return (
            <Chip
              key={item.id}
              label={item.label}
              color={active || completed ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => {
                if (item.id === "patient") {
                  handleBackToPatient();
                  return;
                }
                if (item.id === "document" && selectedPatient?.patientId) {
                  setStep("document");
                  handleCloseReissueSession();
                }
              }}
              clickable={
                item.id === "patient" ||
                (item.id === "document" && !!selectedPatient?.patientId)
              }
              sx={{ fontWeight: active ? 600 : 400 }}
            />
          );
        })}
      </Stack>

      {step === "patient" ? (
        <ReissueSelectPatientStep
          selectedPatient={selectedPatient}
          onSelectAndContinue={handleSelectPatient}
        />
      ) : step === "document" && selectedPatient?.patientId ? (
        <ReissueSelectDocumentStep
          patientId={selectedPatient.patientId}
          patientName={selectedPatient.name}
          onBackToPatient={handleBackToPatient}
          onPrepareReissue={handlePrepareReissue}
          refreshSignal={refreshSignal}
        />
      ) : null}

      {reissueSessionOpen && selectedPatient?.patientId && selectedType ? (
        <ReissueEditInterventionsStep
          patientId={selectedPatient.patientId}
          patientName={selectedPatient.name}
          documentType={selectedType}
          documentRow={selectedRow}
          onBack={handleCloseReissueSession}
          onDone={handleSignDone}
          onFinished={refreshDocuments}
        />
      ) : null}
    </Stack>
  );
}

export default memo(ScheduleDocumentReissuePanel);
